/**
 * Puente de mando (solo admin): tipos de lo que devuelve el servidor
 * (sync-server/src/app-rumbo-puente.ts) y ayudas para pintarlo.
 */
import type { TFunction } from 'i18next';

export type Semaforo = 'verde' | 'ambar' | 'rojo';

export const TRAMOS = ['beta', 'fundador', 'singladura', 'normal'] as const;
export type Tramo = (typeof TRAMOS)[number];

export type FilaPersona = {
  id: string;
  nombre: string | null;
  correo: string | null;
  rol: string;
  activa: boolean;
  alta: number | null;
  ultimaEntrada: number | null;
  ultimoUso: number | null;
  ultimoCambio: number | null;
  diasSinUsar: number | null;
  base: 'uso' | 'solo-abre' | 'nunca';
  semaforo: Semaforo;
  plan: string;
  fundador: boolean;
  tramo: Tramo | null;
  estadoPlan: 'prueba' | 'activo' | 'caducado';
  gratisHasta: number | null;
  pagadoHasta: number | null;
  borradoEl: number | null;
  aBordo: boolean;
  presupuestos: number;
  compartidos: number;
  cifrado: boolean;
  banco: number;
  permisoUso: boolean;
  version: string | null;
  aparato: string | null;
  fallos7: number;
  diasActivos30: number;
  peticionesNuevas: number;
  ultimoContacto: number | null;
};

export type Recuento = { clave: string; veces: number; personas?: number };

/** Lo de una encuesta de Gavi (sync-server/src/rumbo-encuesta.ts). */
export type ResumenEncuesta = {
  respondidas: number;
  recuentos: Record<string, Recuento[]>;
  textos: Array<{
    pregunta: string;
    texto: string;
    userId: string;
    nombre: string | null;
    actualizado: number;
  }>;
  contacto: Array<{ userId: string; nombre: string | null }>;
};

export type Peticion = {
  id: string;
  userId?: string;
  nombre?: string | null;
  tipo: 'fallo' | 'idea';
  texto: string;
  pantalla: string | null;
  version: string | null;
  creado: number;
  estado: 'nueva' | 'contestada' | 'hecha';
  /** Móvil y navegador («Android · Chrome 154»), si lo mandó. */
  aparato?: string | null;
  /** 1 si lleva foto de la pantalla (se borra al marcarla hecha). */
  foto?: number | boolean;
};

export type ResumenPuente = {
  ahora: number;
  totales: {
    personas: number;
    usan7: number;
    usan30: number;
    enfriandose: number;
    enRojo: number;
    pagan: number;
    conPermisoUso: number;
    fallos7: number;
    peticionesNuevas: number;
  };
  personas: FilaPersona[];
  fallos: Recuento[];
  uso: {
    pantallasMas: Recuento[];
    pantallasMenos: Recuento[];
    ventanas: Recuento[];
    brujula: Recuento[];
    tarjetas: Recuento[];
    acciones: Recuento[];
    versiones: Recuento[];
  };
  peticiones: Peticion[];
  /** Puede faltar en servidores antiguos. */
  encuestas?: { beta: ResumenEncuesta; dia40: ResumenEncuesta };
  plazas?: PlazasPuente;
  servidor: {
    disco: { libre: number; total: number } | null;
    memoria: number;
    /** Memoria de la máquina (puede faltar en servidores antiguos). */
    memoriaTotal?: number;
    enMarcha: number;
    /** Cuántas personas más caben (puede faltar en servidores antiguos). */
    capacidad?: {
      registradas: number;
      caben: number | null;
      limita: 'disco' | 'memoria' | null;
    };
  };
};

/** Plazas y lista de espera (puede faltar en servidores antiguos). */
export type PlazasPuente = {
  plazas: number;
  ocupadas: number;
  esperando: Array<{
    turno: number;
    correo: string;
    nombre: string | null;
    creado: number;
    novedades: boolean;
  }>;
};

export type FichaPuente = {
  fila: FilaPersona;
  dias: Array<{ dia: string; acciones: number }>;
  acciones: Recuento[];
  pantallas: Recuento[];
  brujula: Recuento[];
  tarjetas: Recuento[];
  fallos: Array<Recuento & { primero: string; ultimo: string }>;
  peticiones: Peticion[];
  contactos: Array<{ ts: number; nota: string | null }>;
};

/** Cada cuánto se vuelve a pedir el Puente mientras está abierto. */
export const REFRESCO_MS = 60_000;

/** Nombre o, si no hay, el correo. */
export function nombreDe(p: Pick<FilaPersona, 'nombre' | 'correo'>): string {
  return p.nombre?.trim() || p.correo || '?';
}

/** Inicial para el círculo de la persona. */
export function inicialDe(p: Pick<FilaPersona, 'nombre' | 'correo'>): string {
  return nombreDe(p).charAt(0).toUpperCase();
}

/** ¿Coincide con lo buscado (nombre o correo, sin tildes ni mayúsculas)? */
export function coincide(p: FilaPersona, busqueda: string): boolean {
  const limpio = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();
  const b = limpio(busqueda.trim());
  if (!b) {
    return true;
  }
  return limpio(`${p.nombre ?? ''} ${p.correo ?? ''}`).includes(b);
}

/** Correos de una lista, separados por comas (para pegar en Gmail). */
export function correosDe(personas: FilaPersona[]): string {
  return personas
    .map(p => p.correo)
    .filter((c): c is string => !!c && c.includes('@'))
    .join(', ');
}

/**
 * Días usados por semana, de la más antigua a esta, a partir de los días
 * con algún recuento. `semanas` semanas que acaban en `hoy` (AAAA-MM-DD).
 */
export function diasPorSemana(
  dias: Array<{ dia: string }>,
  hoy: string,
  semanas = 8,
): number[] {
  const fin = Date.parse(`${hoy}T12:00:00Z`);
  const cuentas = new Array<number>(semanas).fill(0);
  for (const { dia } of dias) {
    const hace = Math.round(
      (fin - Date.parse(`${dia}T12:00:00Z`)) / 86_400_000,
    );
    const semana = semanas - 1 - Math.floor(hace / 7);
    if (hace >= 0 && semana >= 0) {
      cuentas[semana] += 1;
    }
  }
  return cuentas;
}

/** Hoy en España (AAAA-MM-DD), como cuenta los días el servidor. */
export function hoyMadrid(ahora = Date.now()): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Madrid',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(ahora));
}

/** «hoy», «ayer», «hace 5 días» o «nunca». */
export function haceDias(t: TFunction, dias: number | null): string {
  if (dias == null) {
    return t('never');
  }
  if (dias <= 0) {
    return t('today');
  }
  if (dias === 1) {
    return t('yesterday');
  }
  return t('{{count}} days ago', { count: dias });
}

/** Días desde una fecha en segundos epoch. */
export function diasDesde(ts: number | null, ahora: number): number | null {
  return ts == null ? null : Math.max(0, Math.floor((ahora - ts) / 86_400));
}

/** Fecha corta (27 oct) de unos segundos epoch. */
export function fechaCorta(ts: number | null, locale: string): string {
  if (ts == null) {
    return '';
  }
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
  }).format(new Date(ts * 1000));
}

/** Nombres de las pantallas (las rutas que apunta la app). */
export function nombrePantalla(t: TFunction, ruta: string): string {
  const nombres: Record<string, string> = {
    '/inicio': t('Home'),
    '/budget': t('Plan'),
    '/gastos': t('Spending'),
    '/accounts': t('Accounts'),
    '/accounts/:id': t('An account'),
    '/transactions/:id': t('Edit transaction'),
    '/transactions/new': t('New transaction'),
    '/categories/:id/ficha': t('Category card'),
    '/categories/:id': t('A category'),
    '/mas': t('More'),
    '/settings': t('Settings'),
    '/revisar': t('Review'),
    '/reports': t('Reports'),
    '/ahorros': t('Savings goals'),
    '/bitacora': t('Logbook'),
    '/schedules': t('Fixed payments'),
    '/rules': t('Rules'),
    '/payees': t('Payees'),
    '/nomina': t('Payslip'),
    '/compartido': t('Shared budget'),
    '/puente': t('Command bridge'),
  };
  return nombres[ruta] ?? ruta;
}

/** Un fallo legible: «Aviso rojo · sync.apply-failure», «Error · …». */
export function nombreFallo(t: TFunction, clave: string): string {
  const [tipo, ...resto] = clave.split(':');
  const detalle = resto.join(':');
  const tipos: Record<string, string> = {
    aviso: t('Red notice'),
    error: t('App error'),
    copia: t('Backup'),
    banco: t('Bank'),
  };
  return `${tipos[tipo] ?? tipo} · ${detalle}`;
}

/** Plan visible en la lista: admin, periodo terminado, prueba o tramo. */
export function nombrePlan(t: TFunction, p: FilaPersona): string {
  if (p.rol === 'ADMIN') {
    return t('Administrator');
  }
  if (p.estadoPlan !== 'activo' || p.aBordo) {
    return nombreEstadoPlan(t, p);
  }
  return nombreTramo(t, p.tramo);
}

/** Estado de la cuenta: en prueba, activa, a bordo o periodo terminado. */
export function nombreEstadoPlan(t: TFunction, p: FilaPersona): string {
  if (p.estadoPlan === 'caducado') {
    return t('Period ended');
  }
  if (p.estadoPlan === 'prueba') {
    return t('Free trial');
  }
  return p.aBordo ? t('Active · on board') : t('Active');
}

/** Nombre del tramo (sin tramo: aún no ha pagado). */
export function nombreTramo(t: TFunction, tramo: Tramo | null): string {
  switch (tramo) {
    case 'beta':
      return t('Beta');
    case 'fundador':
      return t('Founding tier');
    case 'singladura':
      return t('Singladura');
    case 'normal':
      return t('Regular');
    default:
      return t('No tier');
  }
}

/** Tipo de aparato legible: Android, iPhone u ordenador. */
export function nombreAparato(t: TFunction, aparato: string | null): string {
  const nombres: Record<string, string> = {
    android: 'Android',
    ios: 'iPhone',
    escritorio: t('Computer'),
    movil: t('Phone'),
  };
  return aparato ? (nombres[aparato] ?? aparato) : '';
}

/** Megas o gigas para la salud del servidor. */
export function tamano(bytes: number): string {
  if (bytes >= 1024 ** 3) {
    const gigas = (bytes / 1024 ** 3).toLocaleString(undefined, {
      maximumFractionDigits: 1,
    });
    return `${gigas} GB`;
  }
  return `${Math.round(bytes / 1024 ** 2)} MB`;
}
